/** Dialog wrapper for create/edit forms with consistent footer and error display. */
import { useBlocker, useRouter } from "@tanstack/react-router";
import { type ReactElement, type ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/shared/ui/button";
import { Dialog } from "@/shared/ui/primitives";

/** Props for `FormDialog`. */
export interface FormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  /** Called on submit; return value ignored — close the dialog yourself on success. */
  onSubmit: () => Promise<unknown> | undefined;
  submitLabel?: string;
  submitting?: boolean;
  /** Disable the submit button (e.g. required pickers empty). */
  disabled?: boolean;
  /** Form-level error (rendered above the footer). */
  error?: string;
  children: ReactNode;
  /** Wider layout for editors. */
  wide?: boolean;
  /** Override automatic DOM change tracking (for editors or staged evidence). */
  dirty?: boolean;
  /** Release staged resources only after an explicit discard confirmation. */
  onDiscard?: () => void | Promise<void>;
}

/** Compare control values, treating asynchronously inserted fields as initially pristine. */
function formChanged(
  form: HTMLFormElement,
  initial: WeakMap<Element, string>,
  edited: WeakSet<Element>,
): boolean {
  let changed = false;
  for (const node of form.querySelectorAll("input,textarea,select,[role=combobox]")) {
    const value = JSON.stringify(
      node instanceof HTMLInputElement
        ? [node.value, node.checked]
        : node instanceof HTMLTextAreaElement || node instanceof HTMLSelectElement
          ? node.value
          : node.textContent,
    );
    if (!initial.has(node) || !edited.has(node)) initial.set(node, value);
    else if (initial.get(node) !== value) changed = true;
  }
  return changed;
}

/** Guard browser unload and client-side navigation only while unsaved edits exist. */
function NavigationGuard({
  active,
  message,
  discard,
  submitting,
}: {
  active: boolean;
  message: string;
  discard: () => Promise<boolean>;
  submitting: () => boolean;
}): null {
  useBlocker({
    disabled: !active,
    enableBeforeUnload: active,
    shouldBlockFn: async () => {
      if (submitting()) return false;
      if (!globalThis.confirm(message)) return true;
      return !(await discard());
    },
  });
  return null;
}

/** Modal form with Cancel/Save footer. */
export function FormDialog({
  open,
  onOpenChange,
  title,
  description,
  onSubmit,
  submitLabel,
  submitting,
  disabled,
  error,
  children,
  wide,
  dirty,
  onDiscard,
}: FormDialogProps): ReactElement {
  const { t } = useTranslation();
  const router = useRouter({ warn: false });
  const initial = useRef(new WeakMap<Element, string>());
  const edited = useRef(new WeakSet<Element>());
  const formRef = useRef<HTMLFormElement | null>(null);
  const submitInFlight = useRef(false);
  const [changed, setChanged] = useState(false);
  const [discardError, setDiscardError] = useState<string>();
  const isDirty = dirty ?? changed;
  useEffect(() => {
    if (!open) {
      initial.current = new WeakMap();
      edited.current = new WeakSet();
      setChanged(false);
    }
  }, [open]);
  const discard = async (): Promise<boolean> => {
    try {
      await onDiscard?.();
      return true;
    } catch (reason) {
      setDiscardError(String(reason));
      return false;
    }
  };
  const requestClose = (nextOpen: boolean): void => {
    if (nextOpen) {
      onOpenChange(true);
      return;
    }
    const populated =
      dirty ??
      (isDirty ||
        (formRef.current !== null &&
          formChanged(formRef.current, initial.current, edited.current)));
    if (submitting || (populated && !globalThis.confirm(t("app.discardChanges")))) return;
    void discard().then((allowed) => {
      if (allowed) {
        initial.current = new WeakMap();
        edited.current = new WeakSet();
        setChanged(false);
        onOpenChange(false);
      }
    });
  };
  return (
    <>
      {router ? (
        <NavigationGuard
          active={open && isDirty}
          message={t("app.discardChanges")}
          discard={discard}
          submitting={() => submitInFlight.current}
        />
      ) : null}
      <Dialog
        open={open}
        onOpenChange={requestClose}
        title={title}
        description={description}
        wide={wide}
      >
        <form
          ref={(form) => {
            formRef.current = form;
            if (form) formChanged(form, initial.current, edited.current);
          }}
          onFocusCapture={(event) => {
            formChanged(event.currentTarget, initial.current, edited.current);
          }}
          onChangeCapture={(event) => {
            edited.current.add(event.target);
            const form = event.currentTarget;
            setTimeout(() => setChanged(formChanged(form, initial.current, edited.current)), 0);
          }}
          onInputCapture={(event) => {
            edited.current.add(event.target as Element);
            const form = event.currentTarget;
            setTimeout(() => setChanged(formChanged(form, initial.current, edited.current)), 0);
          }}
          onClickCapture={(event) => {
            const form = event.currentTarget;
            setTimeout(() => setChanged(formChanged(form, initial.current, edited.current)), 0);
          }}
          onSubmit={(e) => {
            e.preventDefault();
            if (submitInFlight.current) return;
            submitInFlight.current = true;
            void Promise.resolve()
              .then(onSubmit)
              .catch((reason: unknown) => {
                setDiscardError(String(reason));
              })
              .finally(() => {
                submitInFlight.current = false;
              });
          }}
          className="space-y-4"
          noValidate
        >
          {children}
          {error || discardError ? (
            <p role="alert" className="text-sm text-destructive">
              {error ?? discardError}
            </p>
          ) : null}
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={submitting}
              onClick={() => requestClose(false)}
            >
              {t("app.cancel")}
            </Button>
            <Button type="submit" disabled={(submitting ?? false) || (disabled ?? false)}>
              {submitLabel ?? t("app.save")}
            </Button>
          </div>
        </form>
      </Dialog>
    </>
  );
}
